import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  type SortChangedEvent,
} from 'ag-grid-community';
import { trendzGridTheme } from '@components/grid/trendzGridTheme';
import '@components/grid/agGridTheme.css';
import { buildMarketPricesColumns } from '../columns';
import type {
  MarketPrice,
  MarketPriceSortKey,
  SortDirection,
} from '../schema';
import { MARKET_PRICE_SORT_KEYS } from '../schema';
import type { CommonResponse } from '@/types/api';

// AllCommunityModule pulls in column-state APIs (api.getColumnState used by
// readSort) plus everything else community. v33 splits APIs into modules and
// would otherwise raise error #200 when getColumnState is called.
ModuleRegistry.registerModules([AllCommunityModule]);

interface Props {
  data: CommonResponse<MarketPrice> | undefined;
  onSortChange: (sort: { sortBy?: MarketPriceSortKey; sortDir?: SortDirection }) => void;
  onEdit: (row: MarketPrice) => void;
}

const DEFAULT_COL_DEF = {
  resizable: true,
  // Whitelisted columns opt in via `sortable: true`. Anything else is
  // explicitly non-sortable to avoid the "looks-sorted-but-isn't" trap.
  sortable: false,
  filter: false,
  minWidth: 80,
  // Show a faded ⇅ indicator on sortable columns even before they're sorted,
  // so editors can see at a glance which headers are clickable. ag-grid only
  // renders this when `sortable: true`, so non-sortable cols stay clean.
  unSortIcon: true,
  // NOTE: previously had `comparator: () => 0` here to suppress client-side
  // re-sort, but it appears to also suppress the onSortChanged event in
  // ag-grid v33. Letting client-side sort happen briefly — the visible-page
  // reorder is correct on its own (numeric/date fields compare correctly),
  // and the server refetch replaces with the global ordering immediately.
};

const SORT_KEY_SET = new Set<string>(MARKET_PRICE_SORT_KEYS);

function readSort(event: SortChangedEvent): {
  sortBy?: MarketPriceSortKey;
  sortDir?: SortDirection;
} {
  const state = event.api.getColumnState();
  const active = state.find(
    (c) => c.sort != null && c.colId && SORT_KEY_SET.has(c.colId),
  );
  if (!active || !active.sort || !active.colId) return {};
  return {
    sortBy: active.colId as MarketPriceSortKey,
    sortDir: active.sort as SortDirection,
  };
}

export function MarketPricesGrid({ data, onSortChange, onEdit }: Props) {
  const columnDefs = useMemo(
    () => buildMarketPricesColumns({ onEdit }),
    [onEdit],
  );

  return (
    <div className="h-full w-full">
      <AgGridReact<MarketPrice>
        theme={trendzGridTheme}
        rowData={data?.rowData ?? []}
        columnDefs={columnDefs}
        defaultColDef={DEFAULT_COL_DEF}
        animateRows
        getRowId={(p) => p.data.id}
        suppressCellFocus
        suppressMultiSort
        // Override the theme's 64px default — every cell here is single-line
        // (date, name, number) so 48px fits ~40% more rows on screen without
        // crowding. Other grids (price-series, news-articles) keep the default.
        rowHeight={48}
        onSortChanged={(e) => onSortChange(readSort(e))}
      />
    </div>
  );
}
